import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import {
  helpCategoryFieldValidationSchema,
  HELP_CATEGORY_FIELD_INITIAL_VALUES,
  IHelpCategoryForm,
  handleSubmitHelpCategory,
  fetchHelpCategoryFieldById,
} from "./HelpCategoryFormController";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { ICFormikSwitch, ICFormikTextInput } from "../../core-component/formik";
import { IHelpCategory } from "./HelpCategoryController";
import { hasAnyModification } from "../../utils/AppFunctions";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import ICFormikTextArea from "../../core-component/formik/ICFormikTextIArea";

interface IHelpCategoryFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  helpCategoryToEdit?: IHelpCategory;
}

const HelpCategoryForm: React.FC<IHelpCategoryFormProps> = ({
  handleClose,
  readOnly,
  helpCategoryToEdit,
}) => {
  const { t } = UseTranslationHook();
  const [initialValues, setInitialValues] = useState<IHelpCategoryForm>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  useEffect(() => {
    if (helpCategoryToEdit) {
      getCategoryFieldById(helpCategoryToEdit?.id);
    } else {
      setInitialValues(HELP_CATEGORY_FIELD_INITIAL_VALUES);
    }
  }, []);

  const getCategoryFieldById = async (id: string) => {
    setLoading(true);
    await fetchHelpCategoryFieldById(id, handleClose, setInitialValues);
    setLoading(false);
  };

  const checkFormModification = (values: IHelpCategoryForm) => {
    if (initialValues && hasAnyModification(values, initialValues)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = async (values: IHelpCategoryForm) => {
    await handleSubmitHelpCategory(
      values,
      setSubmitLoading,
      handleClose,
      helpCategoryToEdit?.id
    );
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {initialValues && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={helpCategoryFieldValidationSchema(t)}
              onSubmit={(value) => handleSubmit(value)}
            >
              {(formikProps) => {
                const { handleSubmit, values } = formikProps;
                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("help_category")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName="category_title"
                          placeholder={t("category_title")}
                          required
                          disabled={readOnly}
                        />
                        <ICFormikTextArea
                          {...formikProps}
                          name={"category_description"}
                          label={t("category_description")}
                          required
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikProps}
                          fieldName="display_sequence"
                          placeholder={t("display_sequence")}
                          type="number"
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        {values.id ? (
                          <ICFormikSwitch
                            {...formikProps}
                            name={"is_active"}
                            containerClassName="gap-4 !w-fit"
                            placeholder={t("status")}
                            checkedValue={BIT_VALUE.One}
                            unCheckedValue={BIT_VALUE.Zero}
                            disabled={readOnly}
                          />
                        ) : (
                          <></>
                        )}
                      </div>

                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className="w-full mt-2"
                        >
                          {t("submit")}
                        </ICButton>
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
        </>
      )}
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(confirm) => {
            setShowDiscardWarning(false);
            if (confirm) handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default HelpCategoryForm;
